import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Award, PartyPopper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');
    
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    } else if (orderId && token) {
      // For Mercado Pago success redirect
      fetchOrderData(orderId);
    }
  }, [searchParams, token]);

  const fetchOrderData = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const order = response.data.find(o => o.id === orderId);
      if (order) {
        setOrderData(order);
        setStatus('success');
        clearCart();
        triggerCelebration(order.points_earned);
        // Refresh user data to update points in navbar
        if (refreshUser) refreshUser();
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  const triggerCelebration = (points) => {
    if (points > 0) {
      // Fire confetti! 🎉
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  };

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPaymentData(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
        
        // Fetch order data to get points earned
        if (response.data.order_id) {
          const ordersRes = await axios.get(`${API}/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const order = ordersRes.data.find(o => o.id === response.data.order_id);
          if (order) {
            setOrderData(order);
            triggerCelebration(order.points_earned);
          }
        }
        
        // Refresh user data to update points in navbar
        if (refreshUser) refreshUser();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#2E7DCD] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Procesando pago...</h1>
            <p className="text-slate-500">Por favor espera mientras confirmamos tu pago</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Pago Exitoso!</h1>
            <p className="text-slate-500 mb-4">Tu pedido ha sido confirmado</p>
            
            {/* Points earned celebration */}
            {orderData && orderData.points_earned > 0 && (
              <div className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl p-4 mb-6 text-white animate-bounce-once">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <PartyPopper className="w-6 h-6" />
                  <span className="font-bold text-lg">¡Felicidades!</span>
                  <PartyPopper className="w-6 h-6" />
                </div>
                <p className="text-amber-100 text-sm mb-2">Ganaste puntos con esta compra</p>
                <div className="flex items-center justify-center gap-2">
                  <Award className="w-8 h-8" />
                  <span className="text-3xl font-bold">+{orderData.points_earned}</span>
                  <span className="text-lg">puntos</span>
                </div>
              </div>
            )}
            
            {paymentData && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-slate-500">Total pagado</p>
                <p className="text-2xl font-bold text-[#E52225]">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}
            
            {orderData && !paymentData && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-slate-500">Total pagado</p>
                <p className="text-2xl font-bold text-[#E52225]">
                  ${orderData.total?.toFixed(2)} MXN
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button onClick={() => navigate('/dashboard')} className="w-full btn-primary">
                Ver mis pedidos
              </Button>
              <Button onClick={() => navigate('/productos')} variant="outline" className="w-full">
                Seguir comprando
              </Button>
            </div>
          </>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {status === 'expired' ? 'Sesión Expirada' : 'Tiempo Agotado'}
            </h1>
            <p className="text-slate-500 mb-6">
              {status === 'expired' 
                ? 'La sesión de pago ha expirado. Intenta de nuevo.' 
                : 'No pudimos confirmar tu pago. Revisa tu correo para más detalles.'}
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/carrito')} className="w-full btn-primary">
                Volver al carrito
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Ver mis pedidos
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSuccess;
